import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

const VAT_MULTIPLIER = 1.23;
const SKELETON_ROWS = ["alpha", "bravo", "charlie"];

const formatCurrency = (value: number) =>
	new Intl.NumberFormat("pt-PT", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
	}).format(value);

const normalizeNumber = (value: number | null | undefined) => {
	const parsed = Number(value ?? 0);
	return Number.isNaN(parsed) ? 0 : parsed;
};

type MaterialSummary = {
	description?: string | null;
	brand?: string | null;
	model?: string | null;
	price?: number | null;
	newStock?: number | null;
	usedStock?: number | null;
};

type DailySaleEntry = {
	id?: string | number | null;
	quantity?: number | null;
	material: MaterialSummary;
};

type DailyReportTableProps = {
	newSales: DailySaleEntry[];
	usedSales: DailySaleEntry[];
	isLoading: boolean;
	profit: number;
};

const buildMaterialName = (material: MaterialSummary) => {
	const description = material.description?.trim() ?? "";
	const brand = material.brand?.trim() ?? "";
	const model = material.model?.trim() ?? "";
	const parts = [description, brand, model].filter((value) => value.length > 0);

	return parts.join(", ") || "-";
};

const buildPrice = (material: MaterialSummary, profit: number) => {
	const basePrice = normalizeNumber(material.price);
	const factor = VAT_MULTIPLIER * normalizeNumber(profit || 0);

	return formatCurrency(basePrice * factor);
};

const renderSkeleton = () => (
	<TableBody>
		{SKELETON_ROWS.map((rowKey) => (
			<TableRow key={`report-skeleton-${rowKey}`} className="animate-pulse">
				<TableCell>
					<div className="h-4 w-10 rounded bg-muted" />
				</TableCell>
				<TableCell>
					<div className="h-4 w-56 rounded bg-muted/70" />
				</TableCell>
				<TableCell>
					<div className="h-4 w-16 rounded bg-muted/60" />
				</TableCell>
				<TableCell>
					<div className="h-4 w-16 rounded bg-muted/50" />
				</TableCell>
			</TableRow>
		))}
	</TableBody>
);

type TableSectionProps = {
	title: string;
	sales: DailySaleEntry[];
	isLoading: boolean;
	profit: number;
	stockKey: "newStock" | "usedStock";
};

const TableSection = ({
	title,
	sales,
	isLoading,
	profit,
	stockKey,
}: TableSectionProps) => {
	if (sales.length === 0) {
		return null;
	}

	return (
		<div>
			<h2 className="text-lg font-semibold text-foreground">{title}</h2>
			<div className="overflow-hidden rounded-md border border-border/60">
				<Table>
					<TableHeader>
						<TableRow className="bg-muted/50">
							<TableHead className="w-[90px]">Qnt.</TableHead>
							<TableHead>Descrição</TableHead>
							<TableHead className="w-[90px]">Preço</TableHead>
							<TableHead className="w-[90px]">Stock</TableHead>
						</TableRow>
					</TableHeader>
					{isLoading ? (
						renderSkeleton()
					) : (
						<TableBody>
							{sales.map((sale) => {
								const quantity = normalizeNumber(sale.quantity);
								const stockValue = normalizeNumber(sale.material[stockKey]);
								const rowKeyBase = buildMaterialName(sale.material);
								const rowKey = sale.id ?? `${rowKeyBase}-${quantity}`;

								return (
									<TableRow key={rowKey}>
										<TableCell>{quantity}</TableCell>
										<TableCell>{buildMaterialName(sale.material)}</TableCell>
										<TableCell>{buildPrice(sale.material, profit)}</TableCell>
										<TableCell>{stockValue}</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					)}
				</Table>
			</div>
		</div>
	);
};

export function DailyReportTable({
	newSales,
	usedSales,
	isLoading,
	profit,
}: DailyReportTableProps) {
	return (
		<div className="space-y-6">
			<TableSection
				title="Material Novo"
				sales={newSales}
				isLoading={isLoading}
				profit={profit}
				stockKey="newStock"
			/>
			<TableSection
				title="Material Usado"
				sales={usedSales}
				isLoading={isLoading}
				profit={profit}
				stockKey="usedStock"
			/>
		</div>
	);
}
